import { addDays, addYears, isBefore, isValid, parse } from 'date-fns';
import {
  LEGAL_PERSON,
  NATURAL_PERSON,
} from 'src/models/helperTypes/CustomerItem';
import { boolean, object, ObjectSchema, string, StringSchema } from 'yup';

export const errorMessages = {
  nameNoNumbers: 'El nombre no puede contener números',
  nameRequired: 'El nombre es obligatorio',
  lastNameNoNumbers: 'El apellido no puede contener números',
  lastNameRequired: 'El apellido es obligatorio',
  genderRequired: 'El género es obligatorio',
  dateMalformed: 'La fecha debe tener formato DD/MM/AAAA',
  dateTooYoung: 'Debe ser mayor de 18 años',
  dateRequired: 'La fecha de nacimiento es obligatoria',
  emailRequired: 'El correo es obligatorio',
  emailMalformed: 'Formato de correo inválido',
  phoneRequired: 'El teléfono es obligatorio',
  phoneTooShort: 'El teléfono debe contener mínimo 8 caracteres',
  phoneMalformed: 'El teléfono solo puede contener números',
  phoneStarMalformed: 'El teléfono no puede iniciar con 0',
  phoneTooLong: 'El teléfono debe contener un máximo de 8 caracteres',
  entityName: 'El nombre de la empresa es obligatorio.',
  type: 'El tipo de cliente es obligatorio.',
  emptyIdNumber: 'El número de identificación es requerido',
  dimexFormat: 'Debe iniciar con 1 y contener 12 caracteres',
};

const makeFirstNameValidationLogic = (): StringSchema =>
  string()
    .matches(/^(\D*)$/, { message: errorMessages.nameNoNumbers })
    .required(errorMessages.nameRequired);

const makeLastNameValidationLogic = (): StringSchema =>
  string()
    .matches(/^(\D*)$/, { message: errorMessages.lastNameNoNumbers })
    .required(errorMessages.lastNameRequired);

const makeGenderValidationLogic = (): StringSchema =>
  string().required(errorMessages.genderRequired);

const makeEntityNameValidationLogic = (): StringSchema =>
  string().required(errorMessages.entityName);

const makePhoneValidationLogic = (): StringSchema =>
  string()
    .max(8, errorMessages.phoneTooLong)
    .matches(/^(?!0\d)\d*$/, errorMessages.phoneStarMalformed)
    .matches(/^\d*$/, errorMessages.phoneMalformed);

const makeBirthdayValidationLogic = (
  dateFormat: string,
  maxDate: Date,
): StringSchema =>
  string()
    .test(
      'Check format',
      errorMessages.dateMalformed,
      (birthdayString: string): boolean => {
        const birthdate = parse(birthdayString, dateFormat, new Date());
        /**
         * a date string such as 12/12/1 gets parsed to Wed Dec 12 00001
         * thus we need to ensure the user is inserting full dd/mm/yyyy
         */
        return isValid(birthdate) && birthdayString.length === 10;
      },
    )
    .test(
      'Check date boundary',
      errorMessages.dateTooYoung,
      (birthdayString: string): boolean => {
        const birthdate = parse(birthdayString, dateFormat, new Date());
        return isBefore(birthdate, maxDate);
      },
    )
    .required(errorMessages.dateRequired);

export default function makeValidationSchema(): ObjectSchema {
  const maxDate = addDays(addYears(Date.now(), -18), 1);
  const dateFormat = 'dd/MM/yyyy';

  return object().shape({
    profile: string(),
    type: string().required(errorMessages.type),
    firstName: string().when('type', {
      is: NATURAL_PERSON,
      then: makeFirstNameValidationLogic(),
    }),
    lastName: string().when('type', {
      is: NATURAL_PERSON,
      then: makeLastNameValidationLogic(),
    }),
    entityName: string().when('type', {
      is: LEGAL_PERSON,
      then: makeEntityNameValidationLogic(),
    }),
    email: string().email(errorMessages.emailMalformed),
    phoneNumber: string().when('type', {
      is: LEGAL_PERSON,
      then: makePhoneValidationLogic(),
      otherwise: makePhoneValidationLogic(),
    }),
    driverAndCustomerAreDifferent: boolean().required('Campo requerido'),
    driverFirstName: string().when('driverAndCustomerAreDifferent', {
      is: true,
      then: makeFirstNameValidationLogic(),
    }),
    driverLastName: string().when('driverAndCustomerAreDifferent', {
      is: true,
      then: makeLastNameValidationLogic(),
    }),
    driverGender: string().when('driverAndCustomerAreDifferent', {
      is: true,
      then: makeGenderValidationLogic(),
    }),
    driverBirthday: string().when('driverAndCustomerAreDifferent', {
      is: true,
      then: makeBirthdayValidationLogic(dateFormat, maxDate),
    }),
    driverIdType: string().when('driverAndCustomerAreDifferent', {
      is: true,
      then: string().required('El tipo de identificación es requerido'),
    }),
    driverIdNumber: string().when('driverAndCustomerAreDifferent', {
      is: true,
      then: string()
        .when('driverIdType', {
          is: 'N',
          then: string()
            .test(
              'Check id number format',
              'Formato de cédula inválida',
              (id: string) => /^\d{9}$/.exec(id) !== null && id !== '000000000',
            )
            .required(errorMessages.emptyIdNumber),
        })
        .when('driverIdType', {
          is: 'U',
          then: string()
            .required(errorMessages.emptyIdNumber)
            .test(
              'Check id number format',
              errorMessages.dimexFormat,
              (id: string) => (id.length > 0 ? /^1\d{11}$/.test(id) : true),
            ),
        })
        .when('driverIdType', {
          is: 'P',
          then: string().required(errorMessages.emptyIdNumber),
        }),
      otherwise: string().notRequired(),
    }),
    gender: string().when(['driverAndCustomerAreDifferent'], {
      is: (driverAndCustomerAreDifferent: boolean) =>
        !driverAndCustomerAreDifferent,
      then: makeGenderValidationLogic(),
      otherwise: string(),
    }),
    birthday: string().when(['driverAndCustomerAreDifferent'], {
      is: (driverAndCustomerAreDifferent) => !driverAndCustomerAreDifferent,
      then: makeBirthdayValidationLogic(dateFormat, maxDate),
      otherwise: string(),
    }),
  });
}
